'use client';

import Image from 'next/image';
import Link from 'next/link';
import { MapPin, Home, Search, ArrowLeft } from 'lucide-react';
import { Memorial } from '@/lib/types';
import { formatDate, calculateAge } from '@/lib/utils';

interface MemorialHeroProps {
  memorial: Memorial;
}

export function MemorialHero({ memorial }: MemorialHeroProps) {
  const fullName = `${memorial.firstName || ''} ${memorial.lastName || ''}`.trim();
  const age = calculateAge(memorial.dateOfBirth, memorial.dateOfDeath);

  const birthDateFormatted = formatDate(memorial.dateOfBirth);
  const deathDateFormatted = formatDate(memorial.dateOfDeath);

  return (
    <>
      {/* Navigation Bar */}
      <nav className="bg-white border-b border-memorial-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Link href="/" className="font-serif text-lg text-memorial-primary">
              Gedenkportal
            </Link>
            <div className="flex items-center gap-4">
              <Link
                href="/search"
                className="text-memorial-text-secondary hover:text-memorial-primary transition-colors flex items-center gap-2 text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Alle Gedenkseiten</span>
              </Link>
              <Link
                href="/"
                className="text-memorial-text-secondary hover:text-memorial-primary transition-colors flex items-center gap-2 text-sm"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">Startseite</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

    <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
      {/* Background Image or Gradient */}
      {memorial.heroImagePath ? (
        <>
          <Image
            src={memorial.heroImagePath}
            alt=""
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-memorial-primary via-memorial-primary/90 to-memorial-secondary/80" />
      )}

      {/* Content */}
      <div className="relative z-10 text-center text-white px-4 py-16 max-w-4xl mx-auto">
        {/* Portrait */}
        {memorial.imagePath && (
          <div className="mb-8 animate-fade-in">
            <div className="relative w-40 h-40 md:w-52 md:h-52 mx-auto rounded-full overflow-hidden ring-4 ring-white/30 ring-offset-4 ring-offset-transparent shadow-2xl">
              <Image
                src={memorial.imagePath}
                alt={fullName}
                fill
                className="object-cover"
                priority
              />
            </div>
          </div>
        )}

        {/* Name */}
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-4 animate-fade-in">
          {fullName}
        </h1>

        {/* Dates */}
        <p className="text-xl md:text-2xl text-white/90 mb-2 animate-fade-in">
          {birthDateFormatted} – {deathDateFormatted}
        </p>

        {/* Age */}
        {age && (
          <p className="text-lg text-white/70 mb-6 animate-fade-in">
            ({age} Jahre)
          </p>
        )}

        {/* Location */}
        {memorial.placeOfResidence && (
          <div className="flex items-center justify-center gap-2 text-white/80 animate-fade-in">
            <MapPin className="w-5 h-5" />
            <span>{memorial.placeOfResidence}</span>
          </div>
        )}

        {/* Decorative Divider */}
        <div className="mt-10 flex items-center justify-center gap-3 animate-fade-in">
          <div className="w-12 h-px bg-white/30" />
          <div className="w-2 h-2 rotate-45 bg-memorial-accent" />
          <div className="w-12 h-px bg-white/30" />
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-white/60 rounded-full" />
        </div>
      </div>
    </section>
    </>
  );
}
